import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { PlanTier, resolvePlanTier } from '../commerce-admin/plan-quota.constants';
import { MediaAssetService } from './media-asset.service';

/**
 * How long a deleted file stays recoverable, per plan.
 *
 * One number, in one place, because it is also a promise printed in four
 * places in the merchant UI. A retention window that lives in a cron and a
 * constant and a help string is a window that will eventually disagree with
 * itself.
 *
 * Currently 30 days for everyone, matching what merchants have already been
 * told. The per-plan shape exists because the free tier is where retention
 * hurts most — 100 MB with a 30-day hold means deleting half your library
 * frees nothing for a month — and shortening it there is a one-value change
 * when the copy is ready to follow. See docs/22 §6.
 */
export const TRASH_RETENTION_DAYS: Record<PlanTier, number> = {
  free: Number(process.env.MEDIA_TRASH_RETENTION_DAYS_FREE ?? 30),
  basic: Number(process.env.MEDIA_TRASH_RETENTION_DAYS ?? 30),
  premium: Number(process.env.MEDIA_TRASH_RETENTION_DAYS ?? 30),
};

export function retentionDaysFor(rawPlan: string): number {
  return TRASH_RETENTION_DAYS[resolvePlanTier(rawPlan)];
}

/**
 * A cap per run. A merchant who deletes five thousand images should not make
 * the nightly sweep run long enough to be killed, because a sweep that never
 * finishes is a sweep that never runs for anyone else either.
 */
const PURGE_BATCH = 500;

/**
 * The trash sweep, and emptying trash on demand.
 *
 * **Why this replaces image-trash-purge.service.ts.** That cron swept
 * `ProductImage.isActive = false`, which is a catalogue row. The object in
 * storage belongs to a `MediaAsset`, and nothing ever swept those — so a
 * trashed asset sat in R2 indefinitely, counted against quota indefinitely,
 * and the 30-day promise on the storage page was not being kept for anything
 * uploaded through the media stack. The asset is what occupies storage, so the
 * asset is what has to be swept.
 */
@Injectable()
export class MediaTrashService {
  private readonly logger = new Logger(MediaTrashService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly assets: MediaAssetService
  ) {}

  /**
   * Nightly, at an hour chosen to sit clear of the reconciliation crons rather
   * than to compete with them for the same rows.
   */
  @Cron('15 3 * * *')
  async sweep(): Promise<void> {
    const purged = await this.purgeExpired();
    if (purged.purged || purged.failed) {
      this.logger.log(
        `Trash sweep: purged ${purged.purged}, failed ${purged.failed}, ` +
          `freed ${purged.bytes} bytes`
      );
    }
  }

  /**
   * Purge every trashed asset past its organization's retention window.
   *
   * Retention varies by plan, so the cutoff cannot be a single timestamp in a
   * single query. Assets are grouped by organization and each group gets its
   * own cutoff — one extra query per org with trash, which is a small price
   * for the window being a plan feature rather than a global constant.
   *
   * @param dryRun report what would go without deleting anything. Used for the
   *   first live cycle, so the new sweep can be compared against the old one
   *   before it is allowed to remove a single object.
   */
  async purgeExpired(dryRun = false): Promise<{ purged: number; failed: number; bytes: number }> {
    const orgs = await this.prisma.mediaAsset.groupBy({
      by: ['organizationId'],
      where: { status: 'trashed', trashedAt: { not: null } },
    });

    let purged = 0;
    let failed = 0;
    let bytes = 0;

    for (const { organizationId } of orgs) {
      const org = await this.prisma.organization.findUnique({
        where: { id: organizationId },
        select: { plan: true },
      });
      if (!org) continue;

      const days = retentionDaysFor(org.plan);
      const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

      const expired = await this.prisma.mediaAsset.findMany({
        where: { organizationId, status: 'trashed', trashedAt: { lt: cutoff } },
        select: { id: true, sizeBytes: true },
        take: PURGE_BATCH,
      });

      for (const asset of expired) {
        if (dryRun) {
          purged += 1;
          bytes += asset.sizeBytes;
          continue;
        }
        // purge() decrements the usage counter and leaves a failed delete as
        // `purge_failed` for the retry cron rather than dropping the row.
        if (await this.assets.purge(asset.id)) {
          purged += 1;
          bytes += asset.sizeBytes;
        } else {
          failed += 1;
        }
      }
    }

    return { purged, failed, bytes };
  }

  /**
   * Empty an organization's trash now, releasing the bytes immediately.
   *
   * The escape hatch that makes "trash counts against your quota" liveable: a
   * merchant who is blocked can trade their recovery window for space, at the
   * moment they need it, by choosing to. Without this the honest accounting
   * would just be a month-long wall.
   */
  async empty(organizationId: string): Promise<{ purged: number; failed: number; bytes: number }> {
    const trashed = await this.prisma.mediaAsset.findMany({
      where: { organizationId, status: 'trashed' },
      select: { id: true, sizeBytes: true },
    });

    let purged = 0;
    let failed = 0;
    let bytes = 0;

    for (const asset of trashed) {
      if (await this.assets.purge(asset.id)) {
        purged += 1;
        bytes += asset.sizeBytes;
      } else {
        failed += 1;
      }
    }

    this.logger.log(`Trash emptied for org ${organizationId}: ${purged} purged, ${failed} failed`);
    return { purged, failed, bytes };
  }

  /** What Empty would remove, for the confirmation that names the cost. */
  async summary(organizationId: string): Promise<{
    count: number;
    bytes: number;
    retentionDays: number;
    oldestTrashedAt: Date | null;
  }> {
    const [agg, org, oldest] = await Promise.all([
      this.prisma.mediaAsset.aggregate({
        where: { organizationId, status: 'trashed' },
        _count: { _all: true },
        _sum: { sizeBytes: true },
      }),
      this.prisma.organization.findUnique({
        where: { id: organizationId },
        select: { plan: true },
      }),
      this.prisma.mediaAsset.findFirst({
        where: { organizationId, status: 'trashed' },
        orderBy: { trashedAt: 'asc' },
        select: { trashedAt: true },
      }),
    ]);

    return {
      count: agg._count._all,
      bytes: Number(agg._sum.sizeBytes ?? 0),
      retentionDays: retentionDaysFor(org?.plan ?? 'free'),
      oldestTrashedAt: oldest?.trashedAt ?? null,
    };
  }
}
